import { autoElement } from '../../../webmodule-common/components/src/common/lit-decorator-extended';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { DataEntryTabManagerView } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { SettingsGeneralView } from './settings-general-view';
import { SettingsLocationsView } from './settings-locations-view';
import { SettingsLoginImageBrandingView } from './settings-login-image-view';
import { SettingsLogoBrandingView } from './settings-branding-view';
import { SettingsPricingRulesView } from './settings-pricing-rules-view';
import { SettingsPurchaseOrdersRecipientsView } from './settings-purchase-orders-view';
import { SettingsRegionView } from './settings-region-view';
import { SettingsReportsView } from './settings-reports-view';
import { SettingsStaffView } from './settings-staff-view';
import { SettingsTermsOfUseView } from './settings-terms-of-use-view';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { userDataStore } from '../../supplier/common/current-user-data-store';
import { claimIdentity, userSecurity } from '../../../webmodule-common/other/api/user-security';

export interface SettingsViewChildFactory {
  getGeneralSettingsview(): Promise<SettingsGeneralView>;

  getLogoBrandingView(): Promise<SettingsLogoBrandingView>;

  getLoginImageBrandingView(): Promise<SettingsLoginImageBrandingView>;

  getTermsOfUsView(): Promise<SettingsTermsOfUseView>;

  getRegionsView(): Promise<SettingsRegionView>;

  getPurchaseOrderRecipientsView(): Promise<SettingsPurchaseOrdersRecipientsView>;

  getPricingRulesView(): Promise<SettingsPricingRulesView>;

  getStaffView(): Promise<SettingsStaffView>;

  getLocationsView(): Promise<SettingsLocationsView>;

  getReportsView(): Promise<SettingsReportsView>;
}

export class SettingsViewChildFactoryImpl implements SettingsViewChildFactory {
  async getGeneralSettingsview(): Promise<SettingsGeneralView> {
    return await constructAsync(new SettingsGeneralView());
  }

  async getLogoBrandingView(): Promise<SettingsLogoBrandingView> {
    return await constructAsync(new SettingsLogoBrandingView());
  }

  async getLoginImageBrandingView(): Promise<SettingsLoginImageBrandingView> {
    return await constructAsync(new SettingsLoginImageBrandingView());
  }

  async getTermsOfUsView(): Promise<SettingsTermsOfUseView> {
    return await constructAsync(new SettingsTermsOfUseView());
  }

  async getRegionsView(): Promise<SettingsRegionView> {
    return await constructAsync(new SettingsRegionView());
  }

  async getPurchaseOrderRecipientsView(): Promise<SettingsPurchaseOrdersRecipientsView> {
    return await constructAsync(new SettingsPurchaseOrdersRecipientsView());
  }

  async getPricingRulesView(): Promise<SettingsPricingRulesView> {
    return await constructAsync(new SettingsPricingRulesView());
  }

  async getStaffView(): Promise<SettingsStaffView> {
    return await constructAsync(new SettingsStaffView());
  }

  async getLocationsView(): Promise<SettingsLocationsView> {
    return await constructAsync(new SettingsLocationsView());
  }

  async getReportsView(): Promise<SettingsReportsView> {
    return await constructAsync(new SettingsReportsView());
  }
}

@autoElement()
export class SettingsView extends DataEntryTabManagerView {
  childViewFactory: SettingsViewChildFactory;

  generalSettingsView: SettingsGeneralView | null = null;
  logoBrandingView: SettingsLogoBrandingView | null = null;
  loginImageBrandingView: SettingsLoginImageBrandingView | null = null;
  termsOfUseView: SettingsTermsOfUseView | null = null;
  regionsView: SettingsRegionView | null = null;
  orderRecipientsView: SettingsPurchaseOrdersRecipientsView | null = null;
  pricingRulesView: SettingsPricingRulesView | null = null;
  staffView: SettingsStaffView | null = null;

  locationsView: SettingsLocationsView | null = null;
  reportsView: SettingsReportsView | null = null;

  constructor(childViewFactory: SettingsViewChildFactory) {
    super();

    this.childViewFactory = childViewFactory;
  }

  public async afterConstruction(): Promise<void> {
    await userDataStore.reLoadGlobalSupplier();

    this.generalSettingsView = await this.childViewFactory.getGeneralSettingsview();
    this.logoBrandingView = await this.childViewFactory.getLogoBrandingView();
    this.loginImageBrandingView = await this.childViewFactory.getLoginImageBrandingView();
    this.termsOfUseView = await this.childViewFactory.getTermsOfUsView();
    this.regionsView = await this.childViewFactory.getRegionsView();
    this.orderRecipientsView = await this.childViewFactory.getPurchaseOrderRecipientsView();
    this.pricingRulesView = await this.childViewFactory.getPricingRulesView();
    this.staffView = await this.childViewFactory.getStaffView();
    this.locationsView = await this.childViewFactory.getLocationsView();
    this.reportsView = await this.childViewFactory.getReportsView();

    await super.afterConstruction();
  }

  public getTitle() {
    return '%%supplier%% Settings';
  }

  protected createTabManager(): TabManager {
    // The settings page renders per-tab buttons bottom-right (its legacy
    // pageButtonLocation was right, same as the dealer settings page).
    const rightButtons = { buttonRegion: 'primary' as const };
    // build static pages for each of the configured table settings
    const canEditSettings = userSecurity().claimIsTrue(claimIdentity.supplierAllowEditSettings);
    const canAssignTerritories = userSecurity().claimIsTrue(claimIdentity.supplierAssignTerritories);

    const getInitialPages = (): TabPage[] => {
      const pages: TabPage[] = [];

      // Settings pages - require allowEditSettings claim
      if (canEditSettings) {
        pages.push(this.generalSettingsView!.createTabPage(undefined, rightButtons));
        pages.push(this.logoBrandingView!.createTabPage(undefined, rightButtons));
        pages.push(this.loginImageBrandingView!.createTabPage(undefined, rightButtons));
        pages.push(this.termsOfUseView!.createTabPage(undefined, rightButtons));
        pages.push(this.reportsView!.createTabPage(undefined, rightButtons));
      }

      // Regions/Territories - requires assignTerritories claim
      if (canAssignTerritories) {
        pages.push(this.regionsView!.createTabPage(undefined, rightButtons));
      }

      // More settings pages - require allowEditSettings claim
      if (canEditSettings) {
        pages.push(this.orderRecipientsView!.createTabPage(undefined, rightButtons));
        pages.push(this.pricingRulesView!.createTabPage(undefined, rightButtons));
      }

      // Staff view - always available
      pages.push(this.staffView!.createTabPage(undefined, rightButtons));

      // Locations - require allowEditSettings claim
      if (canEditSettings) {
        pages.push(this.locationsView!.createTabPage(undefined, rightButtons));
      }

      return pages;
    };
    return new TabManager({
      initialIndex: 0,
      pages: () => getInitialPages()
    });
  }

  async prepareForSave(): Promise<void> {
    //DOES NOTHING HANDLED BY CHILDREN
  }

  public internalDataChanged(): boolean {
    return false;
  }
}
